import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { api } from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Loader2, Calendar, AlertCircle, Clock, ChevronRight } from 'lucide-react';
import { format, parseISO } from 'date-fns';

export default function ReviewsDueTile() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [data, setData] = useState({ overdue: [], thisWeek: [], upcoming: [], total: 0 });
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const res = await api.get(token, '/reviews/due-soon?days=14');
      setData(res.data);
    } catch (err) {
      console.error('Reviews due fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) {
    return (
      <div className="rounded-xl border border-[#E6E0D9] bg-white p-6" data-testid="reviews-due-tile">
        <div className="flex justify-center py-4"><Loader2 className="w-5 h-5 animate-spin text-[#4A3B32]" /></div>
      </div>
    );
  }

  if (data.total === 0) return null;

  const renderRow = (rev, tone) => (
    <button
      key={rev.id}
      type="button"
      onClick={() => navigate(`/app/clients/${rev.clientId}/reviews`)}
      className="w-full text-left flex items-center justify-between gap-3 py-2 px-3 rounded-md hover:bg-[#F5F3F0] transition-colors group"
      data-testid={`reviews-due-row-${rev.id}`}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium text-[#2C2420] truncate">{rev.clientName}</span>
          <span className="text-xs text-[#8C8078]">—</span>
          <span className="text-xs text-[#5D524D] truncate">{rev.reviewType}</span>
        </div>
        <div className="flex items-center gap-2 mt-0.5 text-[10px] text-[#8C8078]">
          <span>{format(parseISO(rev.nextReviewDate), 'dd MMM yyyy')}</span>
          {rev.serviceLine && <Badge variant="outline" className="text-[9px] py-0 px-1">{rev.serviceLine}</Badge>}
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Badge className={`text-[10px] ${
          tone === 'overdue' ? 'bg-red-100 text-red-700' :
          tone === 'this-week' ? 'bg-amber-100 text-amber-700' :
          'bg-green-100 text-green-700'
        }`}>
          {tone === 'overdue' ? `${Math.abs(rev.daysUntil)}d overdue` :
            rev.daysUntil === 0 ? 'Today' : `${rev.daysUntil}d`}
        </Badge>
        <ChevronRight className="w-4 h-4 text-[#8C8078] opacity-0 group-hover:opacity-100 transition-opacity" />
      </div>
    </button>
  );

  return (
    <div className="rounded-xl border border-[#E6E0D9] bg-white overflow-hidden" data-testid="reviews-due-tile">
      <div className="px-5 pt-5 pb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Calendar className="w-5 h-5 text-[#4A3B32]" />
          <h3 className="font-heading text-base text-[#2C2420]">Reviews Due</h3>
          <Badge className="bg-[#F0EBE6] text-[#4A3B32] text-[10px]">{data.total}</Badge>
        </div>
        <span className="text-xs text-[#8C8078]">Next {data.windowDays} days</span>
      </div>

      <div className="px-2 pb-3 divide-y divide-[#F0EBE6]">
        {data.overdue.length > 0 && (
          <div className="py-1">
            <div className="flex items-center gap-1.5 px-3 py-1 text-[10px] uppercase tracking-wide text-red-700 font-medium">
              <AlertCircle className="w-3 h-3" /> Overdue ({data.overdue.length})
            </div>
            {data.overdue.slice(0, 5).map(r => renderRow(r, 'overdue'))}
          </div>
        )}
        {data.thisWeek.length > 0 && (
          <div className="py-1">
            <div className="flex items-center gap-1.5 px-3 py-1 text-[10px] uppercase tracking-wide text-amber-700 font-medium">
              <Clock className="w-3 h-3" /> This week ({data.thisWeek.length})
            </div>
            {data.thisWeek.slice(0, 5).map(r => renderRow(r, 'this-week'))}
          </div>
        )}
        {data.upcoming.length > 0 && (
          <div className="py-1">
            <div className="flex items-center gap-1.5 px-3 py-1 text-[10px] uppercase tracking-wide text-green-700 font-medium">
              <Calendar className="w-3 h-3" /> Next 2 weeks ({data.upcoming.length})
            </div>
            {data.upcoming.slice(0, 5).map(r => renderRow(r, 'upcoming'))}
          </div>
        )}
      </div>
    </div>
  );
}
